/**
 * Pre-order Stock Limiter
 * Reads Shopify's own inventory to enforce pre-order oversell limits
 */

class PreorderStockLimiter {
  constructor(container) {
    this.container = container;
    this.variantSku = container.dataset.variantSku;
    this.preorderLimit = parseInt(container.dataset.preorderLimit) || 100;
    this.inventoryPolicy = container.dataset.inventoryPolicy;
    this.inventoryQuantity = parseInt(container.dataset.inventoryQuantity, 10);
    if (Number.isNaN(this.inventoryQuantity)) this.inventoryQuantity = 0;
    this.productHandle = container.dataset.productHandle;
    this.submitButton = container.querySelector('[data-submit-button]');

    // Parse per-variant preorder limit map
    this.variantPreorderMap = {};
    try {
      const mapData = container.dataset.variantPreorderMap;
      if (mapData) {
        this.variantPreorderMap = JSON.parse(mapData);
        console.log('[PreorderStockLimiter] Variant preorder map loaded:', this.variantPreorderMap);
      }
    } catch (e) {
      console.warn('[PreorderStockLimiter] Could not parse variant preorder map');
    }
    this.form = container.querySelector('form[data-type="add-to-cart-form"]') || container.querySelector('form');

    // Get the form ID
    const formId = this.form?.getAttribute('id');
    console.log('[PreorderStockLimiter] Looking for form ID:', formId);

    // Find the visible quantity input - check multiple locations
    // 1. First try: visible input associated with this form via form attribute (standalone quantity-selector)
    if (formId) {
      this.quantityInput = document.querySelector(`input[name="quantity"][form="${formId}"]:not([type="hidden"])`);
      console.log('[PreorderStockLimiter] Found by form attribute:', this.quantityInput);
    }
    // 2. Second try: visible input inside container
    if (!this.quantityInput) {
      this.quantityInput = container.querySelector('input[name="quantity"]:not([type="hidden"])');
      console.log('[PreorderStockLimiter] Found inside container:', this.quantityInput);
    }
    // 3. Third try: any visible quantity input on the page with data-quantity-input attribute
    if (!this.quantityInput) {
      this.quantityInput = document.querySelector('input[name="quantity"][data-quantity-input]:not([type="hidden"])');
      console.log('[PreorderStockLimiter] Found by data-quantity-input:', this.quantityInput);
    }

    console.log('[PreorderStockLimiter] Final quantity input:', this.quantityInput);

    // Replace the browser's generic "Value must be less than or equal to
    // N" bubble with one that explains why - the same native tooltip, just
    // with our own wording.
    if (this.quantityInput) {
      this.quantityInput.addEventListener('input', () => this.updateQuantityValidity());
    }

    // Find the hidden quantity mirror inside the form (for theme.js compatibility)
    this.quantityMirror = this.form?.querySelector('input[name="quantity"][data-quantity-mirror]');
    if (this.quantityMirror && this.quantityInput) {
      // Sync the hidden input with the visible one
      this.syncQuantityMirror();
      this.quantityInput.addEventListener('input', () => this.syncQuantityMirror());
      this.quantityInput.addEventListener('change', () => this.syncQuantityMirror());
    }

    // Store original button text
    const textSpan = this.submitButton?.querySelector('[data-add-to-cart-text]');
    this.originalButtonText = textSpan?.textContent?.trim() || 'Add to Cart';

    this.availableStock = 0;
    this.preorderMaxQty = null;
    this.variantData = this.getVariantData();

    this.init();
  }

  getVariantData() {
    // Try to get variant data from the page
    try {
      const variantDataScript = document.querySelector('[data-selected-variant]');
      if (variantDataScript) {
        return JSON.parse(variantDataScript.textContent);
      }
    } catch (e) {
      console.warn('[PreorderStockLimiter] Could not parse variant data');
    }
    return null;
  }

  async init() {
    if (!this.variantSku) {
      console.warn('[PreorderStockLimiter] No variant SKU found');
      return;
    }

    // Get variant ID for cart checking - most forms use a hidden input,
    // but the sticky bar's form uses a <select> for its variant picker.
    const variantInput = this.form?.querySelector('input[name="id"]') || this.form?.querySelector('select[name="id"]');
    this.variantId = variantInput?.value;

    console.log('[PreorderStockLimiter] Initializing with SKU:', this.variantSku, 'Limit:', this.preorderLimit, 'Variant ID:', this.variantId);

    // Track quantity already in cart for this variant
    this.cartQuantity = 0;
    await this.fetchCartQuantity();
    await this.fetchShopifyStock();
    this.updateButtonState();
    this.setupFormValidation();

    // Listen for THEMEYE variant:changed custom event
    document.addEventListener('variant:changed', (e) => {
      if (e.detail?.variant) {
        this.handleVariantChange(e.detail.variant);
      }
    });

    // Listen for THEMEYE pub/sub variant change
    if (window.FoxTheme?.pubsub?.subscribe) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (payload) => {
        if (payload?.data?.variant) {
          this.handleVariantChange(payload.data.variant);
        }
      });
    }

    // Also listen for the variant input value changes
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput) {
      const observer = new MutationObserver(() => this.handleVariantInputChange());
      observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });
      variantIdInput.addEventListener('change', () => this.handleVariantInputChange());
    }

    // Listen for clicks on variant selectors
    document.addEventListener('click', (e) => {
      if (e.target.closest('variant-selects, [data-option-selector], .swatch-element, .select-popout__option')) {
        setTimeout(() => this.handleVariantInputChange(), 200);
      }
    });

    // Listen for successful cart additions to refresh stock data
    this.setupCartAddListener();
  }

  setupCartAddListener() {
    // Store reference to this for use in callbacks
    const self = this;

    // Watch for cart changes by intercepting fetch to /cart/add
    const originalFetch = window.fetch.bind(window);
    window.fetch = async function(...args) {
      const response = await originalFetch(...args);

      // Check if this was a cart/add request
      const url = args[0]?.toString() || '';
      if (url.includes('/cart/add')) {
        // Clone response to read it without consuming
        const clonedResponse = response.clone();
        try {
          const data = await clonedResponse.json();
          // If successful add, refresh stock after a short delay
          if (data && !data.errors) {
            console.log('[PreorderStockLimiter] Cart add detected, refreshing stock...');
            setTimeout(() => self.refreshStockAfterCartAdd(), 1000);
          }
        } catch (e) {
          // Ignore JSON parse errors
        }
      }

      return response;
    };

    // Also listen for theme's cart events if available
    document.addEventListener('cart:add', () => {
      console.log('[PreorderStockLimiter] Cart add event detected');
      setTimeout(() => this.refreshStockAfterCartAdd(), 1000);
    });

    // Listen for THEMEYE pub/sub cart events
    if (window.FoxTheme?.pubsub?.subscribe) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
        console.log('[PreorderStockLimiter] Cart update event detected');
        setTimeout(() => this.refreshStockAfterCartAdd(), 500);
      });
    }
  }

  async refreshStockAfterCartAdd() {
    console.log('[PreorderStockLimiter] Refreshing stock data...');
    await this.fetchCartQuantity();
    await this.fetchShopifyStock();
    this.updateButtonState();
    this.syncQuantityMirror();
    console.log('[PreorderStockLimiter] Stock refreshed, cart qty:', this.cartQuantity, 'new max:', this.preorderMaxQty);
  }

  async fetchCartQuantity() {
    if (!this.variantId) return;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Cart fetch error');

      const cart = await response.json();
      this.cartQuantity = 0;

      // Find this variant in the cart and sum up quantities
      for (const item of cart.items) {
        if (String(item.variant_id) === String(this.variantId)) {
          this.cartQuantity += item.quantity;
        }
      }

      console.log('[PreorderStockLimiter] Cart quantity for variant:', this.cartQuantity);
    } catch (error) {
      console.warn('[PreorderStockLimiter] Failed to fetch cart:', error);
      this.cartQuantity = 0;
    }
  }

  async fetchShopifyStock() {
    // Start from the value Liquid already rendered for this variant, then
    // try to refresh it from Shopify's own product JSON so switching
    // variants or adding to cart reflects current stock without a full
    // page reload. Falls back to the rendered value if that fetch fails.
    this.availableStock = this.inventoryQuantity;

    if (!this.productHandle || !this.variantId) return;

    try {
      const response = await fetch(`/products/${this.productHandle}.js`);
      if (!response.ok) throw new Error('Product fetch error');

      const product = await response.json();
      const variant = product.variants?.find((v) => String(v.id) === String(this.variantId))
        || product.variants?.find((v) => v.sku === this.variantSku);

      if (variant && typeof variant.inventory_quantity === 'number') {
        this.availableStock = variant.inventory_quantity;
        this.inventoryQuantity = variant.inventory_quantity;
      }

      console.log('[PreorderStockLimiter] Shopify stock:', {
        sku: this.variantSku,
        availableStock: this.availableStock,
        preorderLimit: this.preorderLimit
      });
    } catch (error) {
      console.warn('[PreorderStockLimiter] Failed to refresh Shopify stock, using rendered value:', error);
    }
  }

  updateButtonState() {
    if (!this.submitButton) return;

    const allowsContinueSelling = this.inventoryPolicy === 'continue';
    const isOutOfStock = this.availableStock <= 0;

    if (!allowsContinueSelling) {
      // Native DENY policy - Shopify's own inventory rules already enforce
      // this at /cart/add (that's what truncates an over-large add with
      // "Only N items were added to your cart due to availability"), so no
      // custom cap is needed here either way.
      this.resetButton();
      this.dispatchStockUpdate({ isOutOfStock, isPreorder: false, maxQty: null, cartQuantity: this.cartQuantity });
      console.log('[PreorderStockLimiter] Native inventory policy - no custom cap applied');
      return;
    }

    // From here on, inventory_policy is 'continue' and a preorder limit is
    // configured (the only reason this script runs at all). The cap -
    // stock still on hand plus however much oversell is allowed - applies
    // regardless of whether that combined total is still positive or has
    // already gone negative. It must NOT wait for stock to hit zero first:
    // otherwise a single large order on a still-in-stock item can blow
    // straight through both the real stock and the whole oversell
    // allowance in one add, since nothing would have been checking the
    // two numbers together until then.
    const effectiveAvailable = this.availableStock - (this.cartQuantity || 0);
    const maxSellableQty = this.preorderLimit + effectiveAvailable;

    console.log('[PreorderStockLimiter] Checking button state:', {
      availableStock: this.availableStock,
      cartQuantity: this.cartQuantity,
      effectiveAvailable,
      preorderLimit: this.preorderLimit,
      maxSellableQty
    });

    if (maxSellableQty <= 0) {
      this.disableButton('Out of Stock');
      this.preorderMaxQty = 0;
      this.updateQuantityValidity();
      this.dispatchStockUpdate({ isOutOfStock: true, isPreorder: false, maxQty: 0, cartQuantity: this.cartQuantity });
      console.log('[PreorderStockLimiter] DISABLED - exceeds combined stock + preorder limit (cart qty:', this.cartQuantity, ')');
    } else {
      this.enableButton(isOutOfStock ? 'Add to Cart' : this.originalButtonText);
      this.setMaxQuantity(maxSellableQty);
      this.dispatchStockUpdate({ isOutOfStock: false, isPreorder: isOutOfStock, maxQty: maxSellableQty, cartQuantity: this.cartQuantity });
      console.log(
        '[PreorderStockLimiter]',
        isOutOfStock ? 'PRE-ORDER mode' : 'IN STOCK, capped by oversell limit',
        '- max qty:', maxSellableQty, '(cart qty:', this.cartQuantity, ')'
      );
    }
  }

  dispatchStockUpdate(detail) {
    document.dispatchEvent(new CustomEvent('preorder:stockUpdate', { detail }));
  }

  disableButton(text) {
    if (!this.submitButton) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('disabled');

    const textSpan = this.submitButton.querySelector('[data-add-to-cart-text]');
    if (textSpan && text) {
      textSpan.textContent = text;
    } else if (text) {
      this.submitButton.textContent = text;
    }
  }

  enableButton(text) {
    if (!this.submitButton) return;

    this.submitButton.disabled = false;
    this.submitButton.classList.remove('disabled');

    if (text) {
      const textSpan = this.submitButton.querySelector('[data-add-to-cart-text]');
      if (textSpan) {
        textSpan.textContent = text;
      }
    }
  }

  setMaxQuantity(max) {
    if (!this.quantityInput || max <= 0) return;

    const maxQty = Math.max(1, Math.floor(max));

    // Real cap, both as the native max (so the +/- stepper and browser
    // validation respect it) and as data-preorder-max for the JS-side
    // validatePreorder check, which stays authoritative for the friendly
    // "Max N units available" message on submit.
    this.quantityInput.max = maxQty;
    this.quantityInput.dataset.preorderMax = maxQty;
    this.preorderMaxQty = maxQty;

    // If current value exceeds max, reduce it
    if (parseInt(this.quantityInput.value) > maxQty) {
      this.quantityInput.value = maxQty;
    }

    this.updateQuantityValidity();
  }

  updateQuantityValidity() {
    if (!this.quantityInput) return;

    const requestedQty = parseInt(this.quantityInput.value, 10);

    // this.preorderMaxQty can legitimately be 0 (fully sold out) - check
    // for null/undefined explicitly rather than truthiness, so that case
    // isn't silently treated as "no cap".
    if (this.preorderMaxQty != null && requestedQty > this.preorderMaxQty) {
      const cartMsg = this.cartQuantity > 0 ? ` (${this.cartQuantity} already in cart)` : '';
      this.quantityInput.setCustomValidity(`Only ${this.preorderMaxQty} available for pre-order${cartMsg}`);
      // setCustomValidity alone doesn't refresh an already-showing (or
      // about-to-auto-show) native bubble - same reason cart.js's
      // setValidity() and quick-order-list.js's setInputValidity() both
      // call reportValidity() right after setCustomValidity() elsewhere in
      // this theme. Without it, the browser's own generic range message
      // wins instead of this one.
      this.quantityInput.reportValidity();
    } else {
      this.quantityInput.setCustomValidity('');
    }
  }

  setupFormValidation() {
    // Validation function that can be used by both form submit and button click
    const validatePreorder = (e) => {
      const requestedQty = parseInt(this.quantityInput?.value) || 1;
      const effectiveAvailable = this.availableStock - (this.cartQuantity || 0);
      const maxAllowed = this.preorderLimit + effectiveAvailable;

      console.log('[PreorderStockLimiter] Validating:', { requestedQty, maxAllowed, cartQuantity: this.cartQuantity, preorderMaxQty: this.preorderMaxQty });

      // Check if preorder limit exceeded (no stock available for pre-order)
      if (effectiveAvailable <= -this.preorderLimit) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const cartMsg = this.cartQuantity > 0 ? ` (${this.cartQuantity} already in cart)` : '';
        alert(`Max 0 units available for pre-order${cartMsg}`);
        return false;
      }

      // Check if requested qty exceeds what's available for pre-order
      // (preorderMaxQty can legitimately be 0 - check for null, not truthiness)
      if (this.preorderMaxQty != null && requestedQty > this.preorderMaxQty) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const cartMsg = this.cartQuantity > 0 ? ` (${this.cartQuantity} already in cart)` : '';
        alert(`Max ${this.preorderMaxQty} units available for pre-order${cartMsg}`);
        this.quantityInput.value = this.preorderMaxQty;
        this.syncQuantityMirror(); // Sync the hidden mirror
        return false;
      }

      // Fallback check for out of stock items
      if (this.availableStock <= 0 && requestedQty > maxAllowed) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const cartMsg = this.cartQuantity > 0 ? ` (${this.cartQuantity} already in cart)` : '';
        alert(`Max ${Math.max(0, Math.floor(maxAllowed))} units available for pre-order${cartMsg}`);
        this.quantityInput.value = Math.max(1, Math.floor(maxAllowed));
        this.syncQuantityMirror(); // Sync the hidden mirror
        return false;
      }

      return true;
    };

    // Listen on form submit
    if (this.form) {
      this.form.addEventListener('submit', validatePreorder, true);
    }

    // Also listen on button click (for AJAX add to cart)
    if (this.submitButton) {
      this.submitButton.addEventListener('click', validatePreorder, true);
    }
  }

  async handleVariantChange(variant) {
    if (!variant) return;

    const variantId = String(variant.id);
    console.log('[PreorderStockLimiter] Variant changed:', variant.sku, 'ID:', variantId);

    // Look up per-variant preorder data from the map
    const mapEntry = this.variantPreorderMap[variantId];
    if (mapEntry) {
      this.variantSku = mapEntry.sku;
      this.preorderLimit = mapEntry.limit;
      this.inventoryPolicy = mapEntry.policy;
      this.inventoryQuantity = mapEntry.quantity ?? 0;
      console.log('[PreorderStockLimiter] Using map data - SKU:', this.variantSku, 'Limit:', this.preorderLimit, 'Policy:', this.inventoryPolicy, 'Qty:', this.inventoryQuantity);
    } else if (variant.sku) {
      this.variantSku = variant.sku;
      this.inventoryPolicy = variant.inventory_policy;
      if (typeof variant.inventory_quantity === 'number') this.inventoryQuantity = variant.inventory_quantity;
      console.log('[PreorderStockLimiter] No map entry for variant, using event data - SKU:', this.variantSku);
    } else {
      return;
    }

    this.variantId = variantId;

    await this.fetchCartQuantity();
    await this.fetchShopifyStock();
    this.updateButtonState();
  }

  handleVariantInputChange() {
    setTimeout(async () => {
      // Get the current variant ID - hidden input on most forms, <select> on the sticky bar
      const variantInput = this.form?.querySelector('input[name="id"]') || this.form?.querySelector('select[name="id"]');
      const newVariantId = variantInput?.value;

      if (!newVariantId || newVariantId === String(this.variantId)) return;

      // Try the map first
      const mapEntry = this.variantPreorderMap[newVariantId];
      if (mapEntry) {
        console.log('[PreorderStockLimiter] Variant input changed, using map - SKU:', mapEntry.sku, 'Limit:', mapEntry.limit);
        this.variantSku = mapEntry.sku;
        this.variantId = newVariantId;
        this.preorderLimit = mapEntry.limit;
        this.inventoryPolicy = mapEntry.policy;
        this.inventoryQuantity = mapEntry.quantity ?? 0;
        await this.fetchCartQuantity();
        await this.fetchShopifyStock();
        this.updateButtonState();
      } else {
        // Fallback to page data
        const newVariantData = this.getVariantData();
        if (newVariantData?.sku && newVariantData.sku !== this.variantSku) {
          console.log('[PreorderStockLimiter] Variant input changed, fallback - SKU:', newVariantData.sku);
          this.variantSku = newVariantData.sku;
          this.variantId = newVariantData.id;
          this.inventoryPolicy = newVariantData.inventory_policy;
          if (typeof newVariantData.inventory_quantity === 'number') this.inventoryQuantity = newVariantData.inventory_quantity;
          await this.fetchCartQuantity();
          await this.fetchShopifyStock();
          this.updateButtonState();
        }
      }
    }, 150);
  }

  syncQuantityMirror() {
    if (this.quantityMirror && this.quantityInput) {
      this.quantityMirror.value = this.quantityInput.value;
    }
  }

  resetButton() {
    if (!this.submitButton) return;

    this.submitButton.disabled = false;
    this.submitButton.classList.remove('disabled');

    const textSpan = this.submitButton.querySelector('[data-add-to-cart-text]');
    if (textSpan) {
      textSpan.textContent = this.originalButtonText;
    }

    // Remove max limit
    if (this.quantityInput) {
      this.quantityInput.removeAttribute('max');
      this.quantityInput.removeAttribute('data-max');
      this.quantityInput.setCustomValidity('');
    }
    this.preorderMaxQty = null;
  }
}

// Debug: Confirm script is loaded
console.log('[PreorderStockLimiter] Script loaded');

function initPreorderStockLimiters() {
  const containers = document.querySelectorAll('[data-preorder-stock-limiter]');
  console.log('[PreorderStockLimiter] Found containers:', containers.length);

  if (containers.length === 0) {
    // Debug: Check if product-form exists at all
    const productForms = document.querySelectorAll('product-form');
    console.log('[PreorderStockLimiter] Product forms on page:', productForms.length);
    productForms.forEach((form, i) => {
      console.log(`[PreorderStockLimiter] Form ${i} has data-preorder-stock-limiter:`, form.hasAttribute('data-preorder-stock-limiter'));
    });
  }

  containers.forEach(container => new PreorderStockLimiter(container));
}

// Initialize on DOM ready or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPreorderStockLimiters);
} else {
  // DOM is already ready, initialize immediately
  initPreorderStockLimiters();
}

// Also initialize for dynamically loaded content
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initPreorderStockLimiters);
}
